import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ref, onValue } from "../lib/firebase-database";
import { auth, db } from "../firebase";
import { apiGet, apiPost, apiDelete } from "../lib/api-client";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  isBefore,
  startOfDay,
} from "date-fns";
import { AdminLayout } from "./AdminLayout";
import { useAuth } from "../hooks/useAuth";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";

export default function AdminAvailabilityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [villas, setVillas] = useState([]);
  const [selectedVilla, setSelectedVilla] = useState(null);
  // bookedDates: array of { id, dateKey } — id is the API row PK so we can DELETE
  const [bookedDates, setBookedDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(null); // dateKey currently saving
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const villasRef = ref(db, "villas");
    onValue(villasRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setVillas(Object.entries(data).map(([id, v]) => ({ ...v, id })));
      } else {
        setVillas([]);
      }
    });
  }, []);

  const fetchAvailability = useCallback(async (villaId) => {
    if (!villaId) return;
    setLoading(true);
    try {
      const res = await apiGet("/availability", { villaId, limit: 500 });
      const docs = res?.documents || [];
      const expanded = [];
      docs.forEach((row) => {
        const id = row.id || row.$id;
        const start = row.startDate ? new Date(row.startDate) : null;
        const end = row.endDate ? new Date(row.endDate) : start;
        if (!start) return;
        let cursor = new Date(start);
        const stop = end || start;
        while (cursor <= stop) {
          expanded.push({ id, dateKey: format(cursor, "yyyy-MM-dd") });
          cursor.setDate(cursor.getDate() + 1);
        }
      });
      setBookedDates(expanded);
    } catch (err) {
      console.error("Failed to load availability", err);
      setBookedDates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedVilla) fetchAvailability(selectedVilla.id);
    else setBookedDates([]);
  }, [selectedVilla, fetchAvailability]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const isDateBooked = (date) =>
    bookedDates.some((b) => b.dateKey === format(date, "yyyy-MM-dd"));

  const handleToggleDate = async (date) => {
    if (!selectedVilla) return;
    const dateStr = format(date, "yyyy-MM-dd");
    setUpdating(dateStr);
    try {
      const existing = bookedDates.find((b) => b.dateKey === dateStr);
      if (existing) {
        await apiDelete(`/availability/${encodeURIComponent(existing.id)}`);
      } else {
        await apiPost("/availability", {
          villaId: selectedVilla.id,
          startDate: dateStr,
          endDate: dateStr,
          dateKey: dateStr,
          status: "blocked",
          createdAt: new Date().toISOString(),
        });
      }
      await fetchAvailability(selectedVilla.id);
    } catch (err) {
      console.error("Toggle date failed", err);
      alert(`Failed to update date. ${err?.message || ""}`);
    } finally {
      setUpdating(null);
    }
  };

  const previousMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  const nextMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );

  const handleLogout = () => {
    auth.signOut().then(() => navigate("/login"));
  };

  const filteredVillas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return villas;
    return villas.filter(
      (v) =>
        v.name?.toLowerCase().includes(q) ||
        v.location?.mainCity?.toLowerCase().includes(q) ||
        v.mainCity?.toLowerCase().includes(q)
    );
  }, [villas, searchQuery]);

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6">
        {!selectedVilla ? (
          <>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Villa Availability
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Select a villa to block or open dates.
              </p>
            </div>

            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search villas"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
              />
            </div>

            {filteredVillas.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
                {searchQuery
                  ? `No villas match "${searchQuery}"`
                  : "No villas yet."}
              </div>
            ) : (
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {filteredVillas.map((villa) => (
                  <button
                    key={villa.id}
                    type="button"
                    onClick={() => setSelectedVilla(villa)}
                    className="text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-black"
                  >
                    <div className="text-sm font-semibold text-gray-900 line-clamp-1">
                      {villa.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 line-clamp-1">
                      {villa.location?.mainCity ||
                        villa.mainCity ||
                        villa.address ||
                        "—"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedVilla(null)}
                  className="mt-1 p-1.5 text-gray-700 hover:bg-gray-100 rounded-md"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">
                    {selectedVilla.name}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    Click a date to mark it unavailable or available.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-semibold text-gray-900">
                  {format(currentMonth, "MMMM yyyy")}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={previousMonth}
                    className="p-1.5 text-gray-700 hover:bg-gray-100 rounded-md"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={nextMonth}
                    className="p-1.5 text-gray-700 hover:bg-gray-100 rounded-md"
                    aria-label="Next month"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-1">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div
                    key={d}
                    className="text-center text-[11px] font-medium uppercase tracking-wider text-gray-400 py-1"
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: monthStart.getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-10 sm:h-11" />
                ))}
                {monthDays.map((date, idx) => {
                  const booked = isDateBooked(date);
                  const today = isToday(date);
                  const past = isBefore(date, startOfDay(new Date())) && !today;
                  const dateKey = format(date, "yyyy-MM-dd");
                  const isUpdating = updating === dateKey;
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={loading || isUpdating || past}
                      onClick={() => handleToggleDate(date)}
                      title={
                        past
                          ? "Past date"
                          : booked
                            ? "Mark as available"
                            : "Mark as unavailable"
                      }
                      className={`h-10 sm:h-11 rounded-md flex items-center justify-center text-sm border ${
                        past
                          ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
                          : booked
                            ? "bg-black text-white border-black"
                            : "bg-white text-gray-700 border-gray-200 hover:border-black"
                      } ${today && !booked && !past ? "ring-1 ring-black" : ""} ${
                        isUpdating ? "opacity-60" : ""
                      }`}
                    >
                      {isUpdating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        format(date, "d")
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-black rounded-sm" />
                  <span>Unavailable</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-white border border-gray-300 rounded-sm" />
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-50 border border-gray-100 rounded-sm" />
                  <span>Past</span>
                </div>
                {loading && (
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading...
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
